import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import SkeletonBase from "newui/components/Skeleton";

import type { Company } from "newui/features/Company/types";
import { CompanyContactDetails } from "../CompanyDetails/CompanyContactDetails";
import { CompanyDescriptionExpand } from "../CompanyDetails/CompanyDescriptionExpand";

interface CompanyHeaderProps {
	company: Company;
	isLoading: boolean;
}

export const CompanyHeader: React.FC<CompanyHeaderProps> = ({
	company,
	isLoading,
}) => {
	const globalPresence = company.global_presence?.map((g) => g.name) ?? [];
	const primaryAddress = company.locations?.[0]?.address;

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4 border-b border-gray-200 p-4 lg:hidden">
				<div className="flex items-center gap-4">
					<SkeletonBase width="56px" height="56px" />
					<SkeletonBase width="200px" height="24px" />
				</div>
				<SkeletonBase width="100%" height="40px" />
			</div>
		);
	}

	return (
		<div className="border-b border-gray-200 p-4 lg:hidden">
			{/* Top row: Logo + Name */}
			<div className="flex items-center gap-4">
				<div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-gray-100">
					<FallbackImage
						src={company.logo_path}
						alt={company.name}
						className="h-full w-full object-contain"
					/>
				</div>
				<div className="min-w-0 flex-1">
					<h1 className="truncate font-roboto text-lg font-semibold text-gray-900">
						{company.name}
					</h1>
					{company.years_trading && (
						<p className="text-xs text-gray-500">
							{company.years_trading} years trading
						</p>
					)}
				</div>
			</div>

			{/* Description */}
			<div className="mt-3 pb-3">
				<CompanyDescriptionExpand
					company={company}
					isMobile
				/>
			</div>

			{/* Contact Details */}
			<CompanyContactDetails
				className="mt-3"
				phone={company.phone}
				email={company.email}
				primaryAddress={primaryAddress}
				website={company.website}
				showHeading={false}
				size="sm"
			/>

			{/* Global Presence */}
			{globalPresence.length > 0 && (
				<p className="mt-2 text-xs text-gray-500">
					Global Presence:{" "}
					<span className="font-medium text-gray-700">
						{globalPresence.join(", ")}
					</span>
				</p>
			)}
		</div>
	);
};
